import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageService, { LanguageInfo } from '../services/LanguageService';
import AleaLoader from '../components/AleaLoader/AleaLoader';
import Logger from '../utils/logger';

interface LanguageContextType {
  availableLanguages: LanguageInfo[];
  defaultLanguage: string;
  translations: Record<string, any>;
  loading: boolean;
  error: string | null;
  fromCache: boolean;
  refetch: () => void;
  clearCache: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [availableLanguages, setAvailableLanguages] = useState<LanguageInfo[]>([]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('de');
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState<boolean>(false);


  const fetchLanguageData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get project ID from environment variables
      const projectId = import.meta.env.VITE_LOCALIZELY_PROJECT_ID;
      
      // Auth routes: ?lang= param takes priority for direct load without flash
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const isAuthRoute = /^\/(login|forgot-password|reset-password|register)(\/|$)/.test(pathname);
      const urlLang = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('lang')?.toLowerCase()
        : null;
      const selectedLanguage = localStorage.getItem('selectedLanguage');
      const targetLanguage =
        (isAuthRoute && urlLang) ? urlLang : (selectedLanguage || 'de');
      
      // Always fetch fresh data from API (no cache due to no versioning in Localizely)
      const data = await LanguageService.getLanguageData(
        projectId, 
        targetLanguage // Use selected language instead of hardcoded 'de'
      );

      setAvailableLanguages(data.languages);
      setDefaultLanguage('de'); // System default is always German
      setTranslations(data.translations);
      setFromCache(data.fromCache);

      // Load translations into i18n for the selected/default language
      if (data.translations && Object.keys(data.translations).length > 0) {
        i18n.addResourceBundle(targetLanguage, 'translation', data.translations, true, true);
        
        // Force i18n to refresh and trigger re-render
        await i18n.changeLanguage(targetLanguage);
        
        if (isAuthRoute && urlLang) {
          localStorage.setItem('selectedLanguage', urlLang);
        }
        
        Logger.log(`🌍 Loaded fresh translations and set language to ${targetLanguage}`);
      }

      // Clear inactive language caches to keep only active language
      LanguageService.clearInactiveTranslationCaches(targetLanguage);
      
      Logger.log(`🎯 Language data loaded from ${data.fromCache ? 'cache (fallback)' : 'fresh API'} (${data.languages.length} languages, active: ${targetLanguage})`);
    } catch (err) {
      Logger.warn('Failed to load language data, using fallback:', err);
      
             // Use fallback languages when API fails
       const fallbackLanguages = LanguageService.getFallbackLanguages();
       setAvailableLanguages(fallbackLanguages);
       setDefaultLanguage('de');
      setTranslations({});
      setFromCache(false);
      setError(err instanceof Error ? err.message : 'Failed to load language data');
    } finally {
      setLoading(false);
    }
  };

  const clearCache = () => {
    LanguageService.clearStoredData();
    LanguageService.clearAllTranslationCaches();
    Logger.log('🗑️ All language caches cleared');
  };

  useEffect(() => {
    fetchLanguageData();
  }, []);

  const value: LanguageContextType = {
    availableLanguages,
    defaultLanguage,
    translations,
    loading,
    error,
    fromCache,
    refetch: () => fetchLanguageData(),
    clearCache
  };

  return (
    <LanguageContext.Provider value={value}>
      {loading ? <AleaLoader /> : children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
}; 